// A5 Issue detail (updated 03/Oct/26; round 13 — 04/Oct/26):
//   * layout: snapshot (full width) → Issue Description → Gridline · Location · Target date · Date created →
//     RFI · Drawings; the Resolution log on the right fills the window height, NEWEST ENTRY ON TOP with the
//     add-entry box above it
//   * voiding adds a locked "Voided — reason" log entry (no separate banner); ← / → = previous / next issue;
//     Esc / Backspace always go back to the component list (round 13 #5, #8, #10)
//   * fields are edited IN PLACE: click, type; Enter or click away saves straight away; Esc cancels
//     (status + discipline are the chips next to the code)
//   * resolution-log entries: click a date or note to edit — Enter starts a new line, ✓ saves,
//     clicking away asks "Save changes?" (Save / Discard), Esc cancels; action by is picked from a list
//   * Target date shows "N days overdue" in red when past and still Open / Critical
//   * ← Prev · Issue [n] of N · Next → steps through EVERY issue of the component (code list #6)
//   * v0.6.0 Part 6: snapshot slider (+ add, drop / paste, ⤢ → A5.1 viewer); 📎 / drop / paste on the new log entry,
//     a picture dropped on an existing entry adds (or, after asking, replaces) that entry's one snapshot
//   * v0.7.1 (round 25 #3–#6): the card and its viewer hold the issue's own snapshots (max 4; 🗑 removes after asking); a log
//     entry's picture opens alone; every drop area gets an orange border + label while a picture is over it (A5.3 / A5.3.1);
//     anyone who can view may choose the printed pictures
//   * v0.9.0 (round 30 #5, #6): each log entry is a card with 🗑 (asks first, A5.4; imported entries too; a voided issue's
//     entries stay locked); Ctrl+Z / Ctrl+Y undo and redo this visit's changes (undo.ts) — fields, Print on PDF, adding,
//     editing and deleting entries. A deleted entry's picture is kept until the page is left, so Undo brings it back.
// Viewers see everything read-only. A voided issue is locked (the database enforces this too).
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { isTyping } from "../../../core/esc";
import { files, rpc } from "../../../core/api/backend";
import { useRpc } from "../../../core/api/useRpc";
import { href, navigate } from "../../../core/router";
import { DATE_HINT_ROW, formatDate } from "../../../core/format";
import { useLeaveGuard } from "../../../core/leaveGuard";
import { canEdit } from "../../../core/access";
import { CodeChip, ConfirmSave, DateInput, ErrorBox, Field, InfoIcon, Loading, Modal, PageHeader, Popover, StatusPill, STATUS_CLASS, useOutside, useAskClose } from "../../../core/ui/kit";
import type { DeletedLog, IssueDetail, IssueFields, LogEntry } from "../../../core/api/types";
import { UndoStack, undoKey, type Step } from "../undo";
import { codeForNumber, EDITABLE_STATUSES, overdueText } from "../logic";
import { addSnapshot, dropLabel, pickImage, removeSnapshot, RemoveSnapshot, ReplaceSnapshot, SnapshotSlider, SnapshotViewer, useDropOver, useFileUrl, useIssueSnapshots } from "../components/Snapshots";
import { firstImage } from "../../../core/files";
import { PlacePinModal } from "../components/Plan";
import { toast } from "../../../core/ui/kit";
import type { Snapshot } from "../../../core/api/types";
import { pinReading } from "../grid";
import { Icon } from "../../../core/ui/icons";

type Save = (patch: Partial<IssueFields>) => Promise<void>;
type Deleted = { entry: DeletedLog; file_id: string | null };
/** What the log card needs from the page: record a step for Ctrl+Z, delete / restore an entry (picture kept for Undo). */
export interface LogHistory { push: (s: Step) => void; del: (id: string) => Promise<Deleted>; restore: (d: Deleted) => Promise<void> }
const FIELD_NAME: Record<string, string> = { status: "status", discipline: "discipline", description: "description", gridline: "gridline", location: "location",
  rfi_ref: "RFI reference", drawing_ref: "drawings reference", created_on: "date created", target_date: "target date" };

export function IssuePage({ code, prefix, issue }: { code: string; prefix: string; issue: string }) {
  const { data, error, loading, reload } = useRpc("issues_get", { code, issue });
  const [msg, setMsg] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);
  const snapQ = useIssueSnapshots(data?.issue.id);
  const [viewing, setViewing] = useState<number | null>(null);              // index in the issue's own snapshots
  const [viewingLog, setViewingLog] = useState<Snapshot | null>(null);    // one log entry's picture
  const [removing, setRemoving] = useState<Snapshot | null>(null);        // 🗑 on the card: asking first
  const [uploading, setUploading] = useState(false);
  const [pinning, setPinning] = useState(false);                         // A5.2 Place / move pin
  const pinsQ = useRpc("issues_pins", { code, prefix });                  // v0.7.0: the pin's gridline reading ("use this")
  // v0.9.0 undo / redo for this visit; pictures of deleted entries wait here (log id → file id) until the page is left
  const stack = useRef(new UndoStack()).current;
  const parked = useRef(new Map<string, string>());
  const reloadAll = useRef<() => void>(() => {});
  reloadAll.current = () => { reload(); snapQ.reload(); };
  useEffect(() => () => {
    const ids = [...parked.current.values()];
    if (ids.length) rpc("issues_files_purge", { ids }).then((r) => r.paths.forEach((p) => files.remove(p).catch(() => {}))).catch(() => {});
  }, []);
  const history: LogHistory = {
    push: (st) => stack.push(st),
    del: async (id) => {
      const r = await rpc("issues_delete_log", { id });
      r.purge.forEach((p) => files.remove(p).catch(() => {}));          // pictures parked for more than a day (another visit)
      if (r.file_id) parked.current.set(id, r.file_id);
      return { entry: r.entry, file_id: r.file_id };
    },
    restore: async (d) => { await rpc("issues_restore_log", { entry: d.entry, file_id: d.file_id }); parked.current.delete(d.entry.id); },
  };
  useEffect(() => {
    const on = (e: globalThis.KeyboardEvent) => {
      const k = undoKey(e);
      if (!k || isTyping(e.target as Element) || document.querySelector("[role=dialog], [role=alertdialog]")) return;
      e.preventDefault();
      (k === "undo" ? stack.undo() : stack.redo())
        .then((label) => { toast(label ? `${k === "undo" ? "Undone" : "Redone"}: ${label}` : `No actions left to ${k} on this page`); if (label) reloadAll.current(); })
        .catch((err) => { setMsg((err as Error).message); reloadAll.current(); });
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [stack]);
  // ← / → previous / next issue (code list #8) — not while typing or with a menu / dialog open
  useEffect(() => {
    const on = (e: globalThis.KeyboardEvent) => {
      if (!data || data.issue.code !== issue || e.defaultPrevented || isTyping(e.target as Element) || e.altKey || e.ctrlKey || e.metaKey) return;
      if (document.querySelector(".pop-menu, .switcher-menu, .profile-menu, [role=dialog], [role=alertdialog]")) return;
      const to = e.key === "ArrowLeft" ? data.prev : e.key === "ArrowRight" ? data.next : null;
      if (to) { e.preventDefault(); navigate({ name: "issue", code, prefix, issue: to }); }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [data, code, prefix, issue]);
  if (loading && !data) return <Loading />;
  if (error || !data) return <ErrorBox message={error ?? "Not found"} />;
  const i = data.issue;
  const locked = !canEdit(data.access) || i.status === "Voided";
  const save: Save = async (patch) => {
    const old = Object.fromEntries(Object.keys(patch).map((k) => [k, i[k as keyof IssueFields] ?? null])) as Partial<IssueFields>;
    try {
      await rpc("issues_update", { id: i.id, ...patch }); setMsg(null); reload();
      const id = i.id;
      stack.push({ label: `${i.code} ${Object.keys(patch).map((k) => FIELD_NAME[k] ?? k).join(", ")}`,
        undo: async () => { await rpc("issues_update", { id, ...old }); }, redo: async () => { await rpc("issues_update", { id, ...patch }); } });
    }
    catch (e) { setMsg((e as Error).message); }
  };
  const disc = data.disciplines.find((d) => d.code === i.discipline);
  const snaps = snapQ.data?.snapshots ?? [];
  const own = snaps.filter((s) => !s.log_id);                             // the card + viewer: issue snapshots only
  const snapEdit = !locked && !!snapQ.data?.can_edit;
  const remove = async (s: Snapshot) => { await removeSnapshot(s.id); toast("Snapshot removed"); snapQ.reload(); };
  // v0.7.0 (board A5): "📍 Pin is at C/17 · use this" when the pin's reading differs from the Gridline field
  const myPin = pinsQ.data?.pins.find((p) => p.code === i.code);
  const pinAt = myPin && myPin.x !== null && pinsQ.data?.grid
    ? pinReading(pinsQ.data.grid.lines, pinsQ.data.grid.zones, Number(myPin.x), Number(myPin.y), pinsQ.data.grid.ratio ?? 0.707) : null;
  /** add a snapshot (issue-level, or one log entry's); errors show in the page message */
  const attach = async (file: File, logId?: string, replace?: boolean) => {
    if (!snapQ.data) return;
    setUploading(true);
    try { await addSnapshot({ issueId: i.id, projectId: snapQ.data.project_id, file, logId, replace }); setMsg(null); toast(replace ? "Snapshot replaced" : "Snapshot added"); snapQ.reload(); }
    catch (e) { setMsg((e as Error).message); }
    finally { setUploading(false); }
  };
  // v0.9.1 (round 32 #10): several pictures dropped on the card go in one after another (the database still stops at 4)
  const attachMany = async (fs: File[]) => {
    if (fs.length === 1) return attach(fs[0]);
    if (!snapQ.data) return;
    setUploading(true); let ok = 0;
    try { for (const f of fs) { await addSnapshot({ issueId: i.id, projectId: snapQ.data.project_id, file: f }); ok++; } setMsg(null); toast(`${ok} snapshots added`); }
    catch (e) { setMsg((ok ? `${ok} added; ` : "") + (e as Error).message); }
    finally { setUploading(false); snapQ.reload(); }
  };
  return (
    <div className="issue-page">
      <PageHeader
        crumb={<>{data.project.name} <CodeChip code={code} /> › <a href={href({ name: "issues", code })}>Issues Tracker</a> ›{" "}
          <a href={href({ name: "component", code, prefix })}>{data.component.name} ({prefix})</a></>}
        title={<>
          <span className="code">{i.code}</span>
          {locked ? <StatusPill status={i.status} /> : (
            <Popover label="Status" className="chip-pop" button={() => <span className={`pill ${STATUS_CLASS[i.status]}`}>{i.status} <Icon name="arrow_drop_down" /></span>}>
              {(close) => EDITABLE_STATUSES.map((s) => <button key={s} className={`menu-item${s === i.status ? " current" : ""}`} onClick={() => { close(); if (s !== i.status) save({ status: s }); }}>{s}</button>)}
            </Popover>)}
          {locked ? <span className="pill p-tag" style={{ color: disc?.colour }}>{i.discipline}</span> : (
            <Popover label="Discipline" className="chip-pop" button={() => <span className="pill p-tag" style={{ color: disc?.colour }}>{i.discipline} <Icon name="arrow_drop_down" /></span>}>
              {(close) => data.disciplines.map((d) => <button key={d.code} className={`menu-item${d.code === i.discipline ? " current" : ""}`} onClick={() => { close(); if (d.code !== i.discipline) save({ discipline: d.code }); }}>{d.code} — {d.name}</button>)}
            </Popover>)}
          <InfoIcon tip={["Click any field to edit it in place.", "Enter or click away saves; Esc cancels.", "← / → previous or next issue (not while typing).", "Esc or Backspace goes back to the component list."]} />
        </>}
        actions={<IssueStepper data={data} code={code} prefix={prefix} />} />
      {!canEdit(data.access) && <p className="muted small">View only — your role cannot edit issues on this project.</p>}
      {msg && <ErrorBox message={msg} />}
      <div className="detail">
        <div className="detail-left">
          <SnapshotSlider className={`wide${uploading ? " busy" : ""}`} code={i.code} snaps={own} canEdit={snapEdit} loading={!snapQ.data} onAdd={(fs) => attachMany(fs)} onOpen={setViewing}
            onRemove={setRemoving} />
          <FieldCard label="Issue Description">
            <InlineText value={i.description} multiline locked={locked} label="Issue Description" required onSave={(v) => save({ description: v })} />
          </FieldCard>
          <div className="grid4 card-row">
            <FieldCard label="Gridline" extra={<button className="btn icon pin-btn mini" title="Place / move pin on plan" aria-label="Place pin" onClick={() => setPinning(true)}><Icon name="place" /></button>}>
              <InlineText value={i.gridline} locked={locked} label="Gridline" onSave={(v) => save({ gridline: v })} />
              {pinAt && pinAt !== i.gridline && <p className="pin-hint" data-testid="pin-hint"><Icon name="place" /> Pin is at {pinAt}{!locked && <> · <button type="button" className="link-btn" onClick={() => save({ gridline: pinAt })}>use this</button></>}</p>}
            </FieldCard>
            <FieldCard label="Location"><InlineText value={i.location} locked={locked} label="Location" onSave={(v) => save({ location: v })} /></FieldCard>
            <FieldCard label="Target date">
              <InlineDate value={i.target_date} locked={locked} label="Target date" onSave={(d) => save({ target_date: d })} />
              {i.overdue_days > 0 && <p className="overdue strong" data-testid="overdue">{overdueText(i.overdue_days, true)}</p>}
            </FieldCard>
            <FieldCard label="Date created">
              <InlineDate value={i.created_on} locked={locked} label="Date created" required onSave={(d) => save({ created_on: d! })} />
            </FieldCard>
          </div>
          <div className="grid2 card-row">
            <FieldCard label="RFI reference"><InlineText value={i.rfi_ref} locked={locked} label="RFI reference" onSave={(v) => save({ rfi_ref: v })} /></FieldCard>
            <FieldCard label="Drawings reference"><InlineText value={i.drawing_ref} locked={locked} label="Drawings reference" onSave={(v) => save({ drawing_ref: v })} /></FieldCard>
          </div>
          {!locked && <button className="btn danger quiet" onClick={() => setVoiding(true)}>Void issue</button>}
        </div>
        <LogCard data={data} locked={locked} onChanged={() => reloadAll.current()} snaps={snaps} snapEdit={snapEdit} attach={attach}
          onOpenSnap={setViewingLog} history={history} />
      </div>
      {pinning && <PlacePinModal code={code} prefix={prefix} issue={{ id: i.id, code: i.code, gridline: i.gridline }} onClose={() => setPinning(false)} onSaved={() => { setPinning(false); pinsQ.reload(); reload(); }} />}
      {viewing !== null && own.length > 0 && <SnapshotViewer code={i.code} snaps={own} start={viewing} onClose={() => setViewing(null)}
        onPrint={snapQ.data ? async (s, on) => {
          await rpc("issues_snapshot_print", { id: s.id, on }); snapQ.reload();
          stack.push({ label: `Print on PDF ${on ? "on" : "off"}`, undo: async () => { await rpc("issues_snapshot_print", { id: s.id, on: !on }); },
            redo: async () => { await rpc("issues_snapshot_print", { id: s.id, on }); } });
        } : undefined}
        onRemove={snapEdit ? remove : undefined} />}
      {viewingLog && <SnapshotViewer code={i.code} snaps={[viewingLog]} start={0} logEntry={formatDate(data.log.find((l) => l.id === viewingLog.log_id)?.entry_date ?? null)}
        onClose={() => setViewingLog(null)} onRemove={snapEdit ? async (s) => { await remove(s); setViewingLog(null); } : undefined} />}
      {removing && <RemoveSnapshot onKeep={() => setRemoving(null)} onRemove={async () => { const s = removing; setRemoving(null); try { await remove(s); } catch (e) { setMsg((e as Error).message); } }} />}
      {voiding && <VoidModal id={i.id} code={i.code} onClose={() => setVoiding(false)} onDone={() => { setVoiding(false); reload(); }} />}
    </div>
  );
}

/** ← Prev · Issue [n] of N · Next → — every issue of the component, ignoring A4 filters (code list #6). */
function IssueStepper({ data, code, prefix }: { data: IssueDetail; code: string; prefix: string }) {
  const [n, setN] = useState(String(data.issue.seq));
  useEffect(() => setN(String(data.issue.seq)), [data.issue.seq]);
  const go = (c: string | null) => c && navigate({ name: "issue", code, prefix, issue: c });
  const jump = () => { const c = codeForNumber(prefix, Number(n), data.count); if (c && c !== data.issue.code) go(c); else setN(String(data.issue.seq)); };
  return (
    <div className="stepper">
      <button className="btn" disabled={!data.prev} onClick={() => go(data.prev)}><Icon name="arrow_back" /> Prev</button>
      <span className="muted">Issue</span>
      <input className="num-input" aria-label="Issue number" value={n} onChange={(e) => setN(e.target.value.replace(/\D/g, ""))}
        onKeyDown={(e) => { if (e.key === "Enter") jump(); if (e.key === "Escape") { e.preventDefault(); setN(String(data.issue.seq)); (e.target as HTMLInputElement).blur(); } }} onBlur={jump} />
      <span className="muted">of {data.count}</span>
      <button className="btn" disabled={!data.next} onClick={() => go(data.next)}>Next <Icon name="arrow_forward" /></button>
    </div>
  );
}

/** A card with a label line and a value line; `extra` (e.g. the pin button) sits in the corner so labels line up (comment #16). */
function FieldCard({ label, children, extra }: { label: ReactNode; children: ReactNode; extra?: ReactNode }) {
  return <div className="field-card"><p className="k">{label}</p>{extra && <span className="field-extra">{extra}</span>}{children}</div>;
}

const stopEsc = (e: KeyboardEvent, cancel: () => void) => { e.preventDefault(); e.stopPropagation(); cancel(); };

/** Issue fields: click to edit; Enter (Ctrl+Enter in the description) or click away saves straight away; Esc cancels. */
function InlineText({ value, onSave, locked, multiline, label, required }: { value: string; onSave: (v: string) => void | Promise<void>; locked: boolean; multiline?: boolean; label: string; required?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const cancelled = useRef(false);
  useEffect(() => setDraft(value), [value]);
  if (!editing) return (
    <button className={`inline-view${multiline ? " multi" : ""}`} disabled={locked} aria-label={`Edit ${label}`} onClick={() => { cancelled.current = false; setEditing(true); }}>
      {value || <span className="faint">–</span>}
    </button>);
  const commit = () => {
    setEditing(false);
    if (cancelled.current) { setDraft(value); return; }
    if (draft !== value && !(required && !draft.trim())) onSave(draft); else setDraft(value);
  };
  const keys = (e: KeyboardEvent) => {
    if (e.key === "Escape") stopEsc(e, () => { cancelled.current = true; (e.target as HTMLElement).blur(); });
    if (e.key === "Enter" && (!multiline || e.ctrlKey || e.metaKey)) { e.preventDefault(); (e.target as HTMLElement).blur(); }
  };
  return multiline
    ? <textarea autoFocus rows={4} aria-label={label} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={keys} onBlur={commit} className="inline-input" />
    : <input autoFocus aria-label={label} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={keys} onBlur={commit} className="inline-input" />;
}

function InlineDate({ value, onSave, locked, label, required }: { value: string | null; onSave: (v: string | null) => void | Promise<void>; locked: boolean; label: string; required?: boolean }) {
  const [editing, setEditing] = useState(false);
  const draft = useRef<string | null>(value);
  const cancelled = useRef(false);
  const bad = useRef(false);                                   // typed text that can't be read stays open, outlined red
  if (!editing) return <button className="inline-view date" disabled={locked} aria-label={`Edit ${label}`} onClick={() => { draft.current = value; cancelled.current = false; setEditing(true); }}>{formatDate(value)}</button>;
  return <DateInput autoFocus ariaLabel={label} value={value} required={required} onChange={(d) => { draft.current = d; }}
    onValidity={(ok) => { bad.current = !ok; }} onPick={() => (document.activeElement as HTMLElement | null)?.blur()}
    onKeyDown={(e) => {
      if (e.key === "Escape") stopEsc(e, () => { cancelled.current = true; (e.target as HTMLElement).blur(); });
      if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLElement).blur(); }
    }}
    onBlur={() => {
      if (bad.current && !cancelled.current) return;
      setEditing(false);
      const d = draft.current;
      if (cancelled.current || d === value || (required && !d)) return;
      onSave(d);
    }} />;
}

/**
 * One editable part of a resolution-log entry (date or note) — comment #21:
 * Enter = new line (note), ✓ = save, click away = "Save changes?" (Save / Discard), Esc = cancel.
 */
function LogEdit({ kind, value, locked, label, onSave }: { kind: "note" | "date"; value: string; locked: boolean; label: string; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [asking, setAsking] = useState(false);
  const [bad, setBad] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => setDraft(value), [value]);
  const stop = () => { setEditing(false); setAsking(false); setDraft(value); };
  const commit = () => { if (bad) { setAsking(false); return; } setEditing(false); setAsking(false); if (draft !== value && draft.trim()) onSave(draft); else setDraft(value); };
  useOutside(box, () => { if (asking) return; if (draft === value) stop(); else setAsking(true); }, editing);
  if (!editing) return (
    <button className={`inline-view${kind === "date" ? " date" : " multi"}`} disabled={locked} aria-label={`Edit ${label}`} onClick={() => setEditing(true)}>
      {kind === "date" ? formatDate(value) : value}
    </button>);
  const keys = (e: KeyboardEvent) => {
    if (e.key === "Escape") stopEsc(e, stop);
    if (e.key === "Enter" && kind === "date") { e.preventDefault(); commit(); }       // in a note, Enter = new line
  };
  return (
    <div className="log-edit" ref={box}>
      {kind === "note"
        ? <textarea autoFocus rows={3} aria-label={label} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={keys} className="inline-input" />
        : <DateInput autoFocus ariaLabel={label} value={draft} required onChange={(d) => d && setDraft(d)} onKeyDown={keys} onValidity={(ok) => setBad(!ok)} />}
      <button className="btn icon ok" aria-label="Save entry" title="Save" onMouseDown={(e) => e.preventDefault()} onClick={commit}><Icon name="check" /></button>
      {asking && <ConfirmSave onSave={commit} onDiscard={stop} onKeep={() => setAsking(false)} />}
    </div>
  );
}

function LogCard({ data, locked, onChanged, snaps, snapEdit, attach, onOpenSnap, history }: {
  data: IssueDetail; locked: boolean; onChanged: () => void;
  snaps: Snapshot[]; snapEdit: boolean; attach: (f: File, logId?: string, replace?: boolean) => Promise<void>; onOpenSnap: (s: Snapshot) => void;
  history: LogHistory;
}) {
  const [deleting, setDeleting] = useState<LogEntry | null>(null);       // A5.4: asking first
  const [entry, setEntry] = useState({ entry_date: data.today as string | null, note: "", action_by: "" });
  const [pending, setPending] = useState<File | null>(null);              // 📎 picture for the new entry
  const [asking, setAsking] = useState<{ file: File; logId: string } | null>(null);   // replace an entry's picture?
  const byLog = new Map(snaps.filter((s) => s.log_id).map((s) => [s.log_id!, s]));
  const dropOn = (logId: string, f: File | null) => { if (!f || !snapEdit) return; if (byLog.has(logId)) setAsking({ file: f, logId }); else attach(f, logId); };
  const [error, setError] = useState<string | null>(null);
  const [badDate, setBadDate] = useState(false);
  const newDrop = useDropOver(snapEdit);
  const list = useRef<HTMLOListElement>(null);
  const [bump, setBump] = useState(0);
  // newest entry on top (round 13): the list opens at the top and returns there after adding
  useEffect(() => { const el = list.current; if (el) el.scrollTop = 0; }, [data.issue.id, data.log.length, bump]);
  const newestFirst = [...data.log].reverse();
  const voided = data.issue.status === "Voided";
  const add = async (): Promise<boolean> => {
    if (badDate) { setError(`Log date: ${DATE_HINT_ROW}`); return false; }
    if (!entry.note.trim()) { setError("Write a note"); return false; }
    try {
      const r = await rpc("issues_add_log", { id: data.issue.id, ...entry });
      setEntry({ ...entry, note: "" }); setError(null); onChanged(); setBump(bump + 1);
      if (pending) { const f = pending; setPending(null); await attach(f, r.id); }
      let gone: Deleted | null = null;
      history.push({ label: `${data.issue.code} new log entry`, undo: async () => { gone = await history.del(r.id); },
        redo: async () => { if (gone) await history.restore(gone); } });
      return true;
    }
    catch (e) { setError((e as Error).message); return false; }
  };
  // a typed log note that was not added yet: leaving asks Save (= Add entry) / Discard / Keep editing
  useLeaveGuard(!!entry.note.trim(), add);
  const update = async (l: LogEntry, patch: Partial<LogEntry>) => {
    const old = { entry_date: l.entry_date, note: l.note, action_by: l.action_by };
    try {
      await rpc("issues_update_log", { id: l.id, ...patch }); setError(null); onChanged(); setBump(bump + 1);
      history.push({ label: `${data.issue.code} log entry ${formatDate(l.entry_date)}`, undo: async () => { await rpc("issues_update_log", { id: l.id, ...old }); },
        redo: async () => { await rpc("issues_update_log", { id: l.id, ...patch }); } });
    }
    catch (e) { setError((e as Error).message); }
  };
  const remove = async (l: LogEntry) => {
    setDeleting(null);
    try {
      let gone = await history.del(l.id);
      setError(null); onChanged(); toast("Log entry deleted");
      history.push({ label: `${data.issue.code} log entry deleted`, undo: async () => { await history.restore(gone); },
        redo: async () => { gone = await history.del(l.id); } });
    } catch (e) { setError((e as Error).message); }
  };
  return (
    <section className="card pad log">
      <h2 className="row between"><span className="row gap6">Resolution log <InfoIcon tip={["Newest entry on top; add new entries in the box above the log.", "Click a date, note or action by to edit it.", "In a note, Enter starts a new line.", "✓ saves; clicking away asks Save or Discard; Esc cancels.", "📎 adds one snapshot to an entry; you can also drag an image onto the note box or paste it.", "The newest one prints on the PDF issue page.", "🗑 deletes an entry and its picture (asks first) — imported entries too.", "Ctrl+Z undoes your last change on this issue (a field, an entry, a delete); Ctrl+Y redoes it.", "Voiding adds a locked entry with the date and reason; it cannot be deleted."]} /></span></h2>
      {error && <ErrorBox message={error} />}
      {voided && <p className="log-locked muted small" data-testid="log-locked"><Icon name="lock" /> Voided issue — the log and fields are locked.</p>}
      {!locked && (
        <div className="log-add">
          <div className="row gap6">
            <DateInput value={entry.entry_date} onChange={(d) => setEntry({ ...entry, entry_date: d })} ariaLabel="Log date" onValidity={(ok) => setBadDate(!ok)} />
            <select aria-label="Action by" value={entry.action_by} onChange={(e) => setEntry({ ...entry, action_by: e.target.value })}>
              <option value="">Action by…</option>{data.action_by.map((a) => <option key={a}>{a}</option>)}
            </select>
          </div>
          <textarea rows={2} placeholder="Add a resolution note… (drop or paste a snapshot here)" aria-label="Log note" value={entry.note} onChange={(e) => setEntry({ ...entry, note: e.target.value })}
            className={newDrop.over ? "drop-over" : undefined} {...newDrop.bind}
            onDrop={(e) => { newDrop.clear(); const f = firstImage(e.dataTransfer); if (f && snapEdit) { e.preventDefault(); setPending(f); } }}
            onPaste={(e) => { const f = firstImage(e.clipboardData); if (f && snapEdit) { e.preventDefault(); setPending(f); } }} />
          <div className="row between">
            <span className="row gap6 min0">
              <button type="button" className="btn icon" aria-label="Add snapshot" disabled={!snapEdit} onClick={async () => { const f = await pickImage(); if (f) setPending(f); }}><Icon name="attach_file" /></button>
              {pending && <span className="pending-snap" data-testid="pending-snap"><span className="ellipsis">{pending.name}</span><button type="button" className="x" aria-label="Remove snapshot" onClick={() => setPending(null)}><Icon name="close" /></button></span>}
            </span>
            <button className="btn primary" onClick={add}>Add entry</button>
          </div>
        </div>
      )}
      <ol data-testid="log-list" ref={list}>{newestFirst.map((l) => {
        const isVoid = voided && l.note.startsWith("Voided — ");
        return (
          <LogItem key={l.id} id={l.id} isVoid={isVoid} canDrop={snapEdit && !isVoid} has={byLog.has(l.id)} onFile={(f) => dropOn(l.id, f)}
            onDelete={!locked && !isVoid ? () => setDeleting(l) : undefined}>
            {isVoid
              ? <><p className="log-date">{formatDate(l.entry_date)} <span aria-label="locked"><Icon name="lock" /></span></p><p className="log-note">{l.note}</p><p className="act-by">Action by: {l.action_by || "–"}</p></>
              : <>
                <LogEdit kind="date" value={l.entry_date} locked={locked} label="Log date" onSave={(d) => update(l, { entry_date: d })} />
                <LogEdit kind="note" value={l.note} locked={locked} label="Log note text" onSave={(v) => update(l, { note: v })} />
                {byLog.has(l.id) && <LogThumb snap={byLog.get(l.id)!} onOpen={() => onOpenSnap(byLog.get(l.id)!)} />}
                <ActionByPick value={l.action_by} options={data.action_by} locked={locked} onPick={(v) => update(l, { action_by: v })} />
              </>}
          </LogItem>);
      })}
        {data.log.length === 0 && <li className="muted">No entries yet.</li>}
      </ol>
      {asking && <ReplaceSnapshot onKeep={() => setAsking(null)} onReplace={() => { const a = asking; setAsking(null); attach(a.file, a.logId, true); }} />}
      {deleting && <DeleteLogModal entry={deleting} onKeep={() => setDeleting(null)} onDelete={() => remove(deleting)} />}
    </section>
  );
}

/** One resolution-log entry; a picture held over it turns it grey with an orange border and says what a drop does (A5.3.1). */
function LogItem({ id, isVoid, canDrop, has, onFile, onDelete, children }: { id: string; isVoid: boolean; canDrop: boolean; has: boolean; onFile: (f: File | null) => void; onDelete?: () => void; children: ReactNode }) {
  const d = useDropOver(canDrop);
  return (
    <li data-log={id} className={`log-card${isVoid ? " void-entry void" : ""}${d.over ? " drop-over" : ""}`} {...d.bind}
      onDrop={(e) => { d.clear(); if (canDrop) { e.preventDefault(); onFile(firstImage(e.dataTransfer)); } }}>
      {d.over && <span className="drop-label small" data-testid="log-drop-label">{dropLabel("log", has)}</span>}
      {onDelete && <button type="button" className="log-del" aria-label="Delete entry" title="Delete entry" data-log-delete="1" onClick={onDelete}><Icon name="delete" /></button>}
      {children}
    </li>);
}

/** A5.4: "Delete this log entry?" — Keep / Delete (round 31 #3: no note line; Ctrl+Z still brings it back). */
function DeleteLogModal({ entry, onKeep, onDelete }: { entry: LogEntry; onKeep: () => void; onDelete: () => void }) {
  return (
    <Modal title="Delete this log entry?" onClose={onKeep} className="small-wide">
      <div className="log-del-preview" data-testid="log-del-preview"><p className="strong">{formatDate(entry.entry_date)}</p><p className="note">{entry.note}</p>
        <p className="act-by">Action by: {entry.action_by || "–"}</p></div>
      <div className="modal-foot"><button className="btn" autoFocus onClick={onKeep}>Keep</button><button className="btn primary danger-fill" onClick={onDelete}>Delete</button></div>
    </Modal>
  );
}

/** A log entry's snapshot: small picture under the note; click opens the viewer (A5.1). */
function LogThumb({ snap, onOpen }: { snap: Snapshot; onOpen: () => void }) {
  const url = useFileUrl(snap.path);
  return <button type="button" className="log-snap" data-log-snap="1" title="Click to open full size" aria-label="Open snapshot" onClick={onOpen}>{url ? <img src={url} alt="" /> : <><Icon name="image" /> Snapshot</>}</button>;
}

function ActionByPick({ value, options, locked, onPick }: { value: string; options: string[]; locked: boolean; onPick: (v: string) => void }) {
  if (locked) return <p className="act-by">Action by: {value || "–"}</p>;
  return (
    <Popover label="Edit action by" className="act-by-pop" button={() => <span className="act-by">Action by: <u>{value || "–"}</u></span>}>
      {(close) => <>
        {options.map((o) => <button key={o} className={`menu-item${o === value ? " current" : ""}`} onClick={() => { close(); if (o !== value) onPick(o); }}>{o}{o === value && <> <Icon name="check" /></>}</button>)}
        <div className="menu-foot"><InfoIcon tip="The list is managed in Project setup." /></div>
      </>}
    </Popover>
  );
}

function VoidModal({ id, code, onClose, onDone }: { id: string; code: string; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { close, confirm } = useAskClose(!!reason.trim(), onClose);      // no "Save" here: voiding is never a default (v0.4.3)
  const go = async () => {
    try { await rpc("issues_void", { id, reason }); onDone(); } catch (e) { setError((e as Error).message); }
  };
  return (
    <Modal title={`Void ${code}?`} onClose={close}>
      <p className="muted">Voiding retires the issue. It stays in the list (greyed out), is excluded from totals, and cannot be edited or re-opened. Its code is never reused.</p>
      {error && <ErrorBox message={error} />}
      <Field label="Reason *"><textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Void reason" /></Field>
      <div className="modal-foot"><button className="btn" onClick={close}>Cancel</button><button className="btn danger" onClick={go}>Void issue</button></div>
      {confirm}
    </Modal>
  );
}
